import { FC, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { getBuscarAgendamento, IScheduleInfo } from "../api/getBuscarAgendamento";
import { ScheduleSituation } from "../api/getMySchedules";
import ButtonTeleconsultation from "../components/buttons/ButtonTeleconsultation";
import { FooterButtonWrapper } from "../components/checkout/CheckoutCommon";
import PageWrapper from "../components/common/PageWrapper";
import ProfessionalProfile from "../components/common/ProfessionalProfile";
import { CalanedarCheckIcon, CancelIcon, CrossedBellIcon, StethoscopeIcon, HeadPhoneIcon, PlayIcon, WifiIcon } from "../components/Icons";
import useTimer from "../hooks/useTimer";
import CancelSchedulingModal from "../modals/CancelSchedulingModal";
import ThemeContext from "../themes/context";
import { formatDate } from "../utils/date-util/dateUtils";
import { useQuery } from "../utils/useQuery";

const PreviewWrapper = styled.div`
	height: 100vh;
	display: flex;
	flex-direction: column;
`;

const CancelScheduleButton = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: center;
	align-items: center;
	align-content: normal;
	border-bottom: 1px solid #e2e4ea;
	font-family: Livvic;
	font-style: normal;
	font-weight: 500;
	font-size: 12px;
	line-height: 165.5%;
	letter-spacing: 0.005em;
	color: ${props => props.theme.colors.yellowVitat || props.theme.colors.secondary};
	gap: 5px;
	padding: 15px;
`;

const SchedulingPreviewBody = styled.div`
	background-color: #ffffff;
	margin-left: 15px;
	margin-right: 15px;
	box-shadow: 0px 4px 39px 6px #00000012;
	border-radius: 10px 10px 0px 0px;
`;

const InformacaoKey = styled.td`
	font-family: Livvic;
	font-style: normal;
	font-weight: bold;
	font-size: 12.5px;
	line-height: 0%;
	text-align: center;
	color: ${props => props.theme.colors.blackVitat || "#002b49"};
	vertical-align: top;
	padding-top: 11px;
	text-align: left;
`;

const InformacaoValue = styled.td`
	font-family: Livvic;
	font-style: normal;
	font-weight: normal;
	font-size: 12.5px;
	line-height: 165.5%;
	letter-spacing: 0.005em;
	color: ${props => props.theme.colors.blackVitat || "#002b49"};
	text-align: left;
	padding-left: 10px;
`;

const PreviewTitle = styled.p`
	margin: 0px;
	margin-top: 25px;
	margin-bottom: 25px;
	font-family: Livvic;
	font-style: normal;
	font-weight: bold;
	font-size: 12px;
	line-height: 165.5%;
	text-align: center;
	letter-spacing: 0.005em;
	color: ${props => props.theme.colors.blackVitat || "#002b49"};
`;

const PreviewWarning = styled.div`
	margin: 0px;
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: center;
	align-content: normal;
	font-family: Livvic;
	font-style: normal;
	font-weight: normal;
	font-size: 12.5px;
	line-height: 165.5%;
	letter-spacing: 0.005em;
	color: ${props => props.theme.colors.blackVitat || "#002b49"};
	text-align: left;
	gap: 12px;
	margin-top: 7px;
	margin-bottom: 7px;
`;

const SchedulingFooter = styled.div`
	background-color: #ffffff;
	border-radius: 0px 0px 10px 10px;
	margin-left: 15px;
	margin-right: 15px;
	height: 18px;
`;

const WarningTime = styled.p`
	font-family: Livvic;
	font-style: normal;
	font-weight: normal;
	font-size: 11px;
	line-height: 165.5%;
	text-align: center;
	letter-spacing: 0.005em;
	color: ${props => props.theme.colors.blackVitat || "rgba(0, 43, 73, 0.54)"};
	margin-top: 30px;
	padding-bottom: 8px;
`;

const ScheduleInfo = styled.table``;

const SchedulePreviewContent = styled.div`
	padding: 23px;
`;

/**
 * ### Tela de preview do atendimento.
 * Mostra dados do atendimento ja agendado e da a opção de entrar no video atendimento
 */
const AssignmentPreviewScreen: FC = () => {
	const { scheduleId, professionalId } = useParams<{ scheduleId: string; professionalId: string }>();
	const queryParam = useQuery();
	const status = queryParam.get("situation") || "";
	const { showTelemedicine, canCancel } = useTimer(status as ScheduleSituation, queryParam.get("startDate") || "");
	const theme = useContext(ThemeContext);

	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [scheduleInfo, setScheduleInfo] = useState<IScheduleInfo>();

	useEffect(() => {
		if (scheduleId) carregaDados(scheduleId);
	}, [scheduleId]);

	useEffect(() => {
		if (showTelemedicine && scheduleId) carregaDados(scheduleId);
	}, [scheduleId, showTelemedicine]);

	const carregaDados = async (idAgendamento: string) => {
		const response = await getBuscarAgendamento(idAgendamento);
		setScheduleInfo(response);
	};

	return (
		<PageWrapper>
			<PreviewWrapper>
				{professionalId && <ProfessionalProfile codProfessional={professionalId} showOfferedProcedures={false} />}

				<SchedulingPreviewBody>
					{canCancel && (status === "Agendado" || status === "Confirmado") && (
						<CancelScheduleButton onClick={() => setIsModalOpen(true)}>
							<CancelIcon color={theme.colors.yellowVitat || theme.colors.secondary} size={20} />
							Cancelar o agendamento
						</CancelScheduleButton>
					)}

					<SchedulePreviewContent>
						{/* TODO: Adicionar skeleton enquanto carrega informações do agendamento */}
						<ScheduleInfo>
							<tbody>
								<tr>
									<InformacaoKey>Data e hora:</InformacaoKey>
									<InformacaoValue>{scheduleInfo?.inicio ? formatDate("EEEE, dd 'de' MMMM 'as' T'h", scheduleInfo.inicio) : ""}</InformacaoValue>
								</tr>
								<tr>
									<InformacaoKey>Procedimento:</InformacaoKey>
									<InformacaoValue>
										{(scheduleInfo?.procedimento && scheduleInfo.procedimento.description) || ""}{" "}
										{scheduleInfo?.duracao ? `(${scheduleInfo.duracao}min)` : ""}
									</InformacaoValue>
								</tr>
							</tbody>
						</ScheduleInfo>
						<PreviewTitle>Instruções para consulta:</PreviewTitle>
						<PreviewWarning>
							<CalanedarCheckIcon color="#323232" size={20} />
							Garanta sua disponibilidade no horário marcado
						</PreviewWarning>
						<PreviewWarning>
							<CrossedBellIcon color="#323232" size={20} />
							Um ambiente silencioso e reservado é recomendado para a consulta
						</PreviewWarning>
						<PreviewWarning>
							<WifiIcon color="#323232" size={20} />
							Certifique-se que uma conexão de internet estável esteja disponível
						</PreviewWarning>
						<PreviewWarning>
							<HeadPhoneIcon color="#323232" size={20} />
							Recomendamos o uso de fones de ouvido para a sua consulta
						</PreviewWarning>
						<PreviewWarning>
							<StethoscopeIcon color="#323232" size={20} />
							Se você tiver disponível, tenha consigo os resultados de seus últimos exames de laboratório
						</PreviewWarning>
					</SchedulePreviewContent>
				</SchedulingPreviewBody>
				<SchedulingFooter>
					<FooterButtonWrapper>
						<ButtonTeleconsultation
							handleClick={() => {
								if (scheduleInfo) window.open("vitat://browser?url=" + scheduleInfo.linkTelemedicine + "?theme=vitat&webview=true", "_self");
							}}
							icon={<PlayIcon color={showTelemedicine ? theme.colors.whiteVitat || "#000000" : theme.colors.brownVitat || "#8D8D8D"} size={20} />}
							label="Teleconsulta"
							enable={showTelemedicine}
						/>
					</FooterButtonWrapper>
				</SchedulingFooter>

				<WarningTime>A sala fica disponível 5 minutos antes do início da consulta</WarningTime>
				{scheduleId && professionalId && scheduleInfo && (
					<CancelSchedulingModal
						isOpen={isModalOpen}
						handleClose={() => {
							setIsModalOpen(false);
						}}
						schedulingId={scheduleId}
						professionalId={professionalId}
						scheduleProcedureId={scheduleInfo.procedimento._id}
					/>
				)}
			</PreviewWrapper>
		</PageWrapper>
	);
};

export default AssignmentPreviewScreen;
